<template>
  <Dialog
    :modelValue="modelValue"
    size="normal"
    @update:modelValue="emit('update:modelValue', $event)"
    @close="emit('close')"
  >
    <div class="dialog">
      <div class="dialog-title">
        <span class="dialog-title-text">{{ t('halloween.login') }}</span>
      </div>
      <div class="dialog-content-wrap">
        <label>{{ t('halloween.account') }}</label>
        <input v-model="account" type="text" :placeholder="t('halloween.accountPlaceholder')">
        <label>{{ t('halloween.code') }}</label>
        <div class="code-row">
          <input v-model="code" type="text" maxlength="6" :placeholder="t('halloween.codePlaceholder')">
          <button type="button" class="send-btn" :disabled="countdown > 0" @click="handleSendCode">
            {{ countdown > 0 ? countdown : t('halloween.sendCode') }}
          </button>
        </div>
        <div class="actions">
          <button type="button" class="cancel-btn" @click="emit('update:modelValue', false)">
            {{ t('halloween.cancel') }}
          </button>
          <button type="button" class="submit-btn" @click="handleLogin">
            {{ t('halloween.submit') }}
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { defineEmits, defineProps, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ta from 'thinkingdata-browser'
import Dialog from '../index.vue'
import HwApi from '@/config/hwActApi'
import { toastService } from '@/utils/toastService'

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'close'])
const { t } = useI18n()

const account = ref('')
const code = ref('')
const countdown = ref(0)
let countdownTimer = null

const handleSendCode = async () => {
  if (!account.value) {
    toastService.show(t('halloween.accountPlaceholder'))
    return
  }
  await HwApi.sendMailCode({ uid: account.value.toUpperCase() })
  countdown.value = 60
  countdownTimer = setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }, 1000)
  toastService.show(t('halloween.codeSent'))
}

const handleLogin = async () => {
  if (!account.value || !code.value) {
    toastService.show(t('halloween.codePlaceholder'))
    return
  }
  const res = await HwApi.loginByMailCode({
    uid: account.value.toUpperCase(),
    code: code.value,
  })
  if (res?.authorization) {
    const uid = res.roleInfo?.uid || ''
    if (uid) ta.login(uid)
    emit('update:modelValue', false)
  }
}

onUnmounted(() => {
  if (countdownTimer) clearInterval(countdownTimer)
})
</script>

<style scoped lang="less">
.dialog {
  overflow: hidden;

  .dialog-title {
    width: 424px;
    height: 77px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: url('~@/views/act/halloween/assets/img/dialog-title-bg.png') no-repeat center center / 100% 100%;
    margin: 0 auto;
    margin-top: -10px;
  }

  .dialog-title-text {
    margin-top: 6px;
    color: #fff;
    font-size: 32px;
    font-weight: 900;
    letter-spacing: 1px;
    text-shadow:
      0 2px 0 #3b2fe0,
      2px 0 0 #3b2fe0,
      -2px 0 0 #3b2fe0,
      0 -2px 0 #3b2fe0,
      0 4px 0 #24168a;
  }

  .dialog-content-wrap {
    width: 608px;
    height: 396px;
    margin: 24px auto 0;
    padding: 28px 36px 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    background: url('~@/views/act/halloween/assets/img/dialog-inner-bg.png') no-repeat center center / 100% 100%;
  }
}

label {
  margin: 0 0 8px;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
}

input {
  width: 100%;
  height: 56px;
  margin-bottom: 16px;
  padding: 0 16px;
  box-sizing: border-box;
  border: 0;
  border-radius: 8px;
  background: rgba(18, 11, 24, 0.55);
  color: #fff;
  font-size: 22px;
}

.code-row {
  display: flex;
  gap: 12px;

  input {
    flex: 1;
    margin-bottom: 0;
  }
}

.send-btn,
.cancel-btn,
.submit-btn {
  border: 0;
  cursor: pointer;
  font-weight: 800;
}

.send-btn {
  flex: none;
  width: 210px;
  min-height: 56px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #ffe94a;
  color: #1d4ed8;
  font-size: 16px;
  line-height: 1.2;
  white-space: normal;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin-top: auto;
}

.cancel-btn {
  width: 220px;
  height: 72px;
  background: url('~@/views/act/halloween/assets/img/reward-ok-bg.png') no-repeat center center / 100% 100%;
  color: #ffff76;
  font-size: 28px;
  filter: grayscale(100%);
}

.submit-btn {
  width: 220px;
  height: 72px;
  background: url('~@/views/act/halloween/assets/img/reward-ok-bg.png') no-repeat center center / 100% 100%;
  color: #ffff76;
  font-size: 28px;
}
</style>
